/* Ключи опубликованной формы (docs/59-studio-backend-contracts.md §1): `fields[]` шаблона — значения строкой,
   `screen_fields[]` контракта экрана — JSON-тип (`value_encoding: "json"`). Экран ставит контрол только по наличию
   ключа в одном из наборов; ключ из спецификации, покрытый полями шаблона, приходит в `screen_contract.covered[]`.
   Значение JSON-ключа хранится во входах формы JSON-строкой: разобрать её в объект перед POST — дело общего
   слоя отправки (studio-command), экран кладёт только опубликованные ключи. */
import type { StudioForm } from '../studio/studio-api';
import { record, rows, text } from '../studio/studio-content';

export interface KeyInfo {
  key: string;
  type: string;
  values: string[];
  required: boolean;
  json: boolean;
  maxLength: number | null;
  /** `default_value` ключа (docs/59 §4: у `mode` экрана reels — `"one"`); нет — null */
  defaultValue: string | null;
}
const defaultOf = (value: unknown) => (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : null);
export function screenFields(form: StudioForm): KeyInfo[] {
  return rows(record(form).screen_fields).flatMap(f =>
    text(f.key)
      ? [
          {
            key: text(f.key),
            type: text(f.type),
            values: Array.isArray(f.values) ? f.values.map(v => (typeof v === 'string' ? v : JSON.stringify(v))) : [],
            required: f.required === true,
            json: f.value_encoding !== 'string',
            maxLength: typeof f.max_length === 'number' ? f.max_length : null,
            defaultValue: defaultOf(f.default_value),
          },
        ]
      : [],
  );
}
/** Ключ формы: сначала поле шаблона, затем поле контракта экрана. */
export function keyInfo(form: StudioForm | null | undefined, key: string): KeyInfo | null {
  if (!form) return null;
  const own = form.fields.find(f => f.key === key);
  if (own)
    return {
      key,
      type: own.type,
      values: own.values,
      required: own.required,
      json: (own as { value_encoding?: string }).value_encoding === 'json',
      maxLength: own.max_length ?? null,
      defaultValue: defaultOf((own as { default_value?: unknown }).default_value),
    };
  return screenFields(form).find(f => f.key === key) ?? null;
}
/** Какие ключи спецификации покрыты полями шаблона (`screen_contract.covered[]`). */
export function coveredBy(form: StudioForm, key: string): string[] {
  const item = rows(record(record(form).screen_contract).covered).find(c => c.key === key);
  return Array.isArray(item?.by) ? item.by.filter((x): x is string => typeof x === 'string') : [];
}
export function readKey(info: KeyInfo | null, inputs: Record<string, string>): unknown {
  if (!info) return undefined;
  const raw = inputs[info.key];
  if (raw === undefined) return undefined;
  if (!info.json) return raw;
  try {
    return JSON.parse(raw);
  } catch {
    /* значение перечисления или id без кавычек JSON («Повторить» из «Истории»: studioPrefill кладёт строковые ключи как есть) */
    return /^[\w-]{1,64}$/.test(raw) ? raw : undefined;
  }
}
export const readText = (info: KeyInfo | null, inputs: Record<string, string>) => {
  const value = readKey(info, inputs);
  return typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
};
export const readList = (info: KeyInfo | null, inputs: Record<string, string>) => {
  const value = readKey(info, inputs);
  if (Array.isArray(value)) return value.filter((x): x is string => typeof x === 'string');
  return typeof value === 'string' && value ? value.split(',').filter(Boolean) : [];
};
type Patch = string | number | boolean | string[] | Record<string, unknown> | null;
export function writeKey(info: KeyInfo, value: Patch): string | null {
  if (value === null || (Array.isArray(value) && !value.length) || value === '') return null;
  if (info.json || (typeof value === 'object' && !Array.isArray(value))) return JSON.stringify(value);
  return Array.isArray(value) ? value.join(',') : String(value);
}
/** Применить правки только к опубликованным ключам (неопубликованный ключ в заказ не уходит). */
export function patchInputs(
  form: StudioForm | null | undefined,
  current: Record<string, string>,
  patch: Record<string, Patch>,
): Record<string, string> {
  const next = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    const info = keyInfo(form, key);
    if (!info) continue;
    const encoded = writeKey(info, value);
    if (encoded === null) delete next[key];
    else next[key] = encoded;
  }
  return next;
}

/** Ключ спецификации или поле шаблона, которое его покрывает (`screen_contract.covered[]`: `style_id` → `style`). */
export function specKey(form: StudioForm | null | undefined, key: string): KeyInfo | null {
  if (!form) return null;
  return keyInfo(form, key) ?? coveredBy(form, key).map(by => keyInfo(form, by)).find((x): x is KeyInfo => !!x) ?? null;
}
/** Правка входов по ключам спецификации: значение уходит в опубликованный ключ (свой или покрывающий). */
export function patchSpec(
  form: StudioForm | null | undefined,
  current: Record<string, string>,
  patch: Record<string, Patch>,
): Record<string, string> {
  const mapped: Record<string, Patch> = {};
  for (const [key, value] of Object.entries(patch)) {
    const info = specKey(form, key);
    if (info) mapped[info.key] = value;
  }
  return patchInputs(form, current, mapped);
}
/** Описание загрузки JSON-ключа (`upload: {kind, purposes, accept}`), если форма его публикует. */
export function screenUpload(form: StudioForm | null | undefined, key: string): { kind: string; accept: string[]; purpose: string } | null {
  if (!form) return null;
  const item = rows(record(form).screen_fields).find(f => f.key === key);
  const upload = record(item?.upload);
  if (!item || !text(upload.kind)) return null;
  const purposes = Array.isArray(upload.purposes) ? upload.purposes.filter((x): x is string => typeof x === 'string') : [];
  return {
    kind: text(upload.kind),
    accept: Array.isArray(upload.accept) ? upload.accept.filter((x): x is string => typeof x === 'string') : [],
    purpose: purposes[0] ?? 'order_input',
  };
}
